import { FilesTableEnum } from '../enums/FilesTableEnum';

export type PdfConversionTerminalOutcome =
  | {
      kind: 'success';
      processId: string;
      newFileName?: string;
      /** Source file title — search fallback when BE omits new file id/name. */
      sourceFileName?: string;
      /** Source file id — reject if BE echoes it as new_file_id. */
      sourceFileId?: number | null;
      newFileId?: number | null;
      destinationFolderId?: number | null;
    }
  | {
      kind: 'failed';
      processId: string;
      errorCode?: string;
      errorMessage?: string;
    }
  | {
      kind: 'timeout';
      processId: string;
    };

/**
 * Coerce payload/folder id: missing → undefined; 0/null/invalid → null (personal root).
 */
export function coercePdfConversionFolderId(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value == null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

/** Positive file id only; missing/invalid → null. */
export function coercePdfConversionFileId(value: unknown): number | null {
  const folderLike = coercePdfConversionFolderId(value);
  return folderLike === undefined ? null : folderLike;
}

export function resolvePdfConversionDestinationFolderId(
  payload: Record<string, unknown>,
  pendingDestinationFolderId?: number | null,
): number | null {
  const fromPayload = coercePdfConversionFolderId(
    payload.destination_folder_id ?? payload.destinationFolderId,
  );
  if (fromPayload !== undefined) {
    return fromPayload;
  }
  return pendingDestinationFolderId ?? null;
}

/**
 * Prefer explicit new-file keys. If only `file_id` is present and it differs from the
 * source file we tracked at convert start, treat it as the converted PDF id.
 */
export function resolvePdfConversionNewFileId(
  payload: Record<string, unknown>,
  sourceFileId?: number | null,
): number | null {
  const explicit = coercePdfConversionFileId(
    payload.new_file_id ??
      payload.newFileId ??
      payload.converted_file_id ??
      payload.convertedFileId ??
      payload.result_file_id ??
      payload.resultFileId,
  );
  // BE sometimes echoes source id on new_* keys — never treat source as the PDF.
  if (explicit != null && (sourceFileId == null || explicit !== sourceFileId)) {
    return explicit;
  }

  const fileId = coercePdfConversionFileId(payload.file_id ?? payload.fileId);
  if (fileId != null && sourceFileId != null && fileId !== sourceFileId) {
    return fileId;
  }
  return null;
}

/** Normalize BE extension (`.pdf` or `pdf`) for comparisons. */
export function normalizeFileExtension(extension?: string | null): string {
  return (extension ?? '').replace(/^\./, '').toLowerCase();
}

export function isPdfFileExtension(extension?: string | null): boolean {
  return normalizeFileExtension(extension) === 'pdf';
}

export function resolvePdfConversionNewFileName(payload: Record<string, unknown>): string {
  const raw = payload.new_file_name ?? payload.newFileName;
  return typeof raw === 'string' ? raw : raw != null ? String(raw) : '';
}

export function buildPdfConversionDestinationPath(
  destinationFolderId: number | null | undefined,
  newFileId?: number | null,
): string {
  const base =
    destinationFolderId != null && destinationFolderId > 0
      ? `/files/${FilesTableEnum.Personal}/folder/${destinationFolderId}`
      : `/files/${FilesTableEnum.Personal}`;

  if (newFileId == null || newFileId <= 0) {
    return base;
  }

  const params = new URLSearchParams({ prependFileId: String(newFileId) });
  return `${base}?${params.toString()}`;
}

/** True when current path is the convert destination (folder or personal root). */
export function isViewingPdfConversionDestination(
  pathname: string,
  destinationFolderId: number | null | undefined,
): boolean {
  return pathname === buildPdfConversionDestinationPath(destinationFolderId);
}
